import { useMemo, useRef, useState } from 'react';
import useShareInvite from './useShareInvite';

const LEVELS = [
  { value: 'full', label: '전체 액세스' },
  { value: 'edit', label: '편집' },
  { value: 'comment', label: '댓글' },
  { value: 'view', label: '보기' },
];

function levelLabel(v) {
  return LEVELS.find((l) => l.value === v)?.label || v;
}

function Avatar({ name, url, type }) {
  if (url) {
    return <img className="si-avatar" src={url} alt="" />;
  }
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <span className={`si-avatar si-avatar-fallback${type === 'group' ? ' is-group' : ''}`}>
      {type === 'group' ? '#' : initial}
    </span>
  );
}

/**
 * 페이지별 권한(선택 공유) 섹션.
 * props: { pageId }
 */
export default function ShareInviteSection({ pageId }) {
  const {
    loading,
    error,
    restricted,
    entries,
    members,
    groups,
    addedKeys,
    setPageRestricted,
    upsertPermission,
    updateLevel,
    removePermission,
  } = useShareInvite(pageId);

  const [query, setQuery] = useState('');
  const [pickLevel, setPickLevel] = useState('edit');
  const [busy, setBusy] = useState(false);
  const blurTimer = useRef(null);
  const [focused, setFocused] = useState(false);

  // 검색 후보: 이름 부분 일치 + 이미 추가된 것 제외
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    const people = members
      .filter((m) => !addedKeys.has(`user:${m.user_id}`))
      .filter((m) => !q || (m.display_name || '').toLowerCase().includes(q))
      .map((m) => ({
        type: 'user',
        id: m.user_id,
        name: m.display_name || '이름 없음',
        avatarUrl: m.avatar_url || null,
      }));
    const grps = groups
      .filter((g) => !addedKeys.has(`group:${g.id}`))
      .filter((g) => !q || (g.name || '').toLowerCase().includes(q))
      .map((g) => ({ type: 'group', id: g.id, name: g.name || '그룹', avatarUrl: null }));
    return [...grps, ...people].slice(0, 12);
  }, [members, groups, addedKeys, query]);

  const onPick = async (cand) => {
    setBusy(true);
    const ok = await upsertPermission(cand.type, cand.id, pickLevel);
    setBusy(false);
    if (ok) {
      setQuery('');
      setFocused(false);
    }
  };

  const onToggleRestrict = async () => {
    setBusy(true);
    await setPageRestricted(!restricted);
    setBusy(false);
  };

  const onLevelChange = async (entry, level) => {
    setBusy(true);
    await updateLevel(entry.principal_type, entry.principal_id, level);
    setBusy(false);
  };

  const onRemove = async (entry) => {
    setBusy(true);
    await removePermission(entry.principal_type, entry.principal_id);
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="sm-loading">
        <div className="spinner" />
      </div>
    );
  }

  const showDropdown = focused && candidates.length > 0;

  return (
    <div className="si-wrap">
      {/* 제한 토글 */}
      <div className="sm-row">
        <div className="sm-row-text">
          <div className="sm-row-title">이 페이지 제한</div>
          <div className="sm-row-sub">
            {restricted
              ? '아래에 지정된 사람·그룹만 접근할 수 있습니다.'
              : '조직 전체가 이 페이지에 접근할 수 있습니다.'}
          </div>
        </div>
        <button
          type="button"
          className={`sm-switch${restricted ? ' is-on' : ''}`}
          onClick={onToggleRestrict}
          disabled={busy}
          role="switch"
          aria-checked={restricted}
          aria-label="이 페이지 제한"
        >
          <span className="sm-knob" />
        </button>
      </div>

      {/* 접근 대상 추가 */}
      <div className="si-add">
        <div className="si-search">
          <input
            className="si-search-inp"
            type="text"
            placeholder="이름 또는 그룹 검색"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              if (blurTimer.current) clearTimeout(blurTimer.current);
              setFocused(true);
            }}
            onBlur={() => {
              blurTimer.current = setTimeout(() => setFocused(false), 140);
            }}
            aria-label="접근 대상 검색"
          />
          <select
            className="si-level-pick"
            value={pickLevel}
            onChange={(e) => setPickLevel(e.target.value)}
            aria-label="추가할 권한 등급"
          >
            {LEVELS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>

        {showDropdown ? (
          <div className="si-dropdown">
            {candidates.map((c) => (
              <button
                type="button"
                key={`${c.type}:${c.id}`}
                className="si-cand"
                // onMouseDown 으로 input blur 보다 먼저 처리
                onMouseDown={(e) => { e.preventDefault(); onPick(c); }}
                disabled={busy}
              >
                <Avatar name={c.name} url={c.avatarUrl} type={c.type} />
                <span className="si-cand-name">{c.name}</span>
                {c.type === 'group' ? <span className="si-cand-tag">그룹</span> : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* 현재 권한 목록 */}
      <div className="si-list">
        {entries.length === 0 ? (
          <div className="si-empty">
            {restricted
              ? '아직 추가된 대상이 없습니다. 지금은 생성자와 관리자만 볼 수 있습니다.'
              : '특정 대상을 추가하면 제한을 켰을 때 적용됩니다.'}
          </div>
        ) : (
          entries.map((e) => (
            <div className="si-item" key={`${e.principal_type}:${e.principal_id}`}>
              <Avatar name={e.name} url={e.avatarUrl} type={e.principal_type} />
              <span className="si-item-name">
                {e.name}
                {e.principal_type === 'group' ? <span className="si-cand-tag">그룹</span> : null}
              </span>
              <select
                className="si-item-level"
                value={e.level}
                onChange={(ev) => onLevelChange(e, ev.target.value)}
                disabled={busy}
                aria-label={`${e.name} 권한 등급`}
              >
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="si-remove"
                onClick={() => onRemove(e)}
                disabled={busy}
                aria-label={`${e.name} 제거`}
                title="제거"
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>

      {error ? <div className="sm-err">{error}</div> : null}
    </div>
  );
}

export { levelLabel };
